import { pickText, type RetailVariantDto } from '@telestore/contracts';
import { compareSizes } from '@telestore/catalog';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { convert, formatMoney } from '@telestore/shared';
import { ratioToCss } from '@telestore/theming';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { cart } from '../lib/cart.js';
import { useShop } from '../lib/shop-context.js';
import { haptic, isMiniApp, setMainButton } from '../lib/telegram.js';
import { ShopFooter, ShopHeader, StickyBar } from '../components/chrome.js';
import { Badge, ComparePrice, ErrorNotice, Money, Spinner } from '../components/primitives.js';
import { Button } from '../components/ui/button.js';
import {
  ModifierPicker,
  defaultSelection,
  missingRequired,
  modifierDelta,
} from '../components/modifier-picker.js';
import { SlotPicker } from '../components/slot-picker.js';
import { booking } from '../lib/booking.js';

/**
 * Product detail.
 *
 * Fashion retail lives and dies on the size/colour picker: a customer who
 * cannot tell what is in stock messages the merchant instead of buying, which
 * is exactly the manual work TeleStore exists to remove. So every option shows
 * its own availability, and out-of-stock combinations are visibly dead rather
 * than silently rejected at checkout.
 */
export function ProductPage() {
  const { slug, productSlug } = useParams({ from: '/s/$slug/p/$productSlug' });
  const { data: shopData, locale, t } = useShop();
  const shop = shopData.shop;
  const layout = shopData.resolvedTheme.layout;
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['product', slug, productSlug],
    queryFn: () => api.product(slug, productSlug),
  });

  const product = query.data?.product;
  const variants = useMemo(() => product?.variants ?? [], [product]);

  const sizes = useMemo(
    () => [...new Set(variants.map((v) => v.size).filter((s): s is string => Boolean(s)))].sort(compareSizes),
    [variants],
  );
  const colors = useMemo(
    () => [...new Set(variants.map((v) => v.color).filter((c): c is string => Boolean(c)))],
    [variants],
  );

  const [size, setSize] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [imageIndex, setImageIndex] = useState(0);

  /* --------------------------------------------------------------- §4 F&B */
  const fnb = product?.fnb ?? null;
  const modifierGroups = useMemo(() => fnb?.modifierGroups ?? [], [fnb]);
  const [modifiers, setModifiers] = useState<Record<string, string[]>>({});

  /**
   * Seed the defaults once the product arrives, keyed on its id so navigating
   * between two dishes does not carry the first one's picks onto the second.
   */
  useEffect(() => {
    setModifiers(defaultSelection(modifierGroups));
  }, [modifierGroups]);

  const unmetGroups = useMemo(
    () => missingRequired(modifierGroups, modifiers),
    [modifierGroups, modifiers],
  );
  const delta = useMemo(
    () => modifierDelta(modifierGroups, modifiers),
    [modifierGroups, modifiers],
  );

  /* ---------------------------------------------------------- §4 services */
  const service = product?.service ?? null;
  const [slot, setSlot] = useState<{ startsAt: string; resourceId: string } | null>(null);

  // Pre-select when there is only one option — an unnecessary tap on a
  // single-colour product is pure friction.
  useEffect(() => {
    if (sizes.length === 1) setSize(sizes[0]!);
    if (colors.length === 1) setColor(colors[0]!);
  }, [sizes, colors]);

  const selected = useMemo<RetailVariantDto | null>(() => {
    if (variants.length === 0) return null;
    return (
      variants.find(
        (variant) =>
          (sizes.length === 0 || variant.size === size) &&
          (colors.length === 0 || variant.color === color),
      ) ?? null
    );
  }, [variants, size, color, sizes.length, colors.length]);

  /**
   * What still stands between the customer and the cart. Each vertical adds
   * its own gate; the wording below picks the first unmet one so the button
   * always says what to do next rather than a generic "select options".
   */
  const needsChoice =
    (variants.length > 0 && !selected) || unmetGroups.length > 0 || (service !== null && !slot);
  /** §4: sold-out is the F&B flag, out-of-stock is the retail count. */
  const soldOut = fnb ? fnb.isSoldOut || !fnb.isSellingNow : selected ? selected.stock <= 0 : false;
  const unitPrice = (selected?.price?.amount ?? product?.price.amount ?? 0) + delta;

  const canAdd = Boolean(product) && !needsChoice && !soldOut;

  function actionLabel(): string {
    if (fnb?.isSoldOut) return t('common.outOfStock');
    if (fnb && !fnb.isSellingNow) return t('storefront.notSellingNow');
    if (soldOut) return t('common.outOfStock');
    if (service && !slot) return t('storefront.chooseTime');
    if (unmetGroups.length > 0) {
      return t('storefront.chooseGroup', { group: pickText(unmetGroups[0]!.name, locale) });
    }
    if (variants.length > 0 && !selected) return t('storefront.selectOptions');
    return service ? t('storefront.bookNow') : t('storefront.addToCart');
  }

  function addToCart() {
    if (!product || !canAdd) return;
    cart.useShop(shop.slug);
    /**
     * §4 services: the chosen slot is not a cart line, it is the whole order.
     * It rides alongside the cart in its own store so checkout can send it as
     * the `booking` field and the server can take the row lock that actually
     * prevents a double booking.
     */
    if (service && slot) {
      booking.set({ productId: product.id, ...slot });
    } else {
      booking.clear();
    }

    cart.add({
      productId: product.id,
      variantId: selected?.id ?? null,
      quantity: 1,
      name: pickText(product.name, locale),
      imageUrl: selected?.imageUrl ?? product.images[0]?.url ?? null,
      unitPrice,
      options: [
        color,
        size,
        // The picks, spelled out, so the cart line reads the way the customer
        // ordered it rather than as a row of opaque ids.
        ...modifierGroups.flatMap((group) =>
          (modifiers[group.id] ?? []).map((optionId) => {
            const option = group.options.find((entry) => entry.id === optionId);
            return option ? pickText(option.name, locale) : null;
          }),
        ),
      ]
        .filter(Boolean)
        .join(' / '),
      slug: product.slug,
      ...(modifierGroups.length > 0 ? { modifiers } : {}),
    });
    haptic('success');
    void navigate({ to: '/s/$slug/cart', params: { slug: shop.slug } });
  }

  // In a Mini App the primary action belongs on Telegram's own MainButton.
  useEffect(() => {
    if (!isMiniApp() || !product) return;
    return setMainButton({
      text: actionLabel(),
      enabled: canAdd,
      onClick: addToCart,
    });
  });

  if (query.isPending) {
    return (
      <div className="min-h-dvh">
        <ShopHeader showBack />
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  if (query.isError || !product) {
    return (
      <div className="min-h-dvh">
        <ShopHeader showBack />
        <ErrorNotice
          message={(query.error as Error | undefined)?.message ?? t('storefront.productUnavailable')}
          onRetry={() => void query.refetch()}
          retryLabel={t('common.retry')}
        />
      </div>
    );
  }

  const images = product.images.length > 0 ? product.images : [];
  const gallery = (
    <div className="relative">
      <div
        className="w-full overflow-hidden"
        style={{
          aspectRatio: ratioToCss(layout.card_image_ratio),
          background: 'var(--color-surface)',
        }}
      >
        {images[imageIndex] ? (
          <img
            src={images[imageIndex]!.url}
            alt={pickText(product.name, locale)}
            className="size-full object-cover"
          />
        ) : null}
      </div>
      {images.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto px-4 py-3">
          {images.map((image, index) => (
            <button
              key={image.url}
              type="button"
              onClick={() => setImageIndex(index)}
              className="size-14 shrink-0 overflow-hidden rounded-[var(--radius-card)]"
              style={{
                outline: index === imageIndex ? '2px solid var(--color-primary)' : 'none',
                outlineOffset: '1px',
              }}
            >
              <img src={image.url} alt="" className="size-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  const info = (
    <div className="flex flex-col gap-4 px-4 pt-4">
      <div>
        <h1 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
          {pickText(product.name, locale)}
        </h1>
        <div className="mt-1 flex items-baseline gap-2">
          <Money amount={unitPrice} currency={shop.currency} className="text-xl font-bold" />
          {product.compareAtPrice ? (
            <ComparePrice amount={product.compareAtPrice.amount} currency={shop.currency} />
          ) : null}
        </div>
        {/*
          Reference conversion (§6.4) — never a second listing price, and never
          computed here: `convert` owns the platform rate and the 100-riel
          rounding rule, `formatMoney` owns the grouping.
        */}
        <p className="mt-1 text-xs opacity-60">
          {t('storefront.referencePrice', {
            amount: formatMoney(
              convert(
                { amount: unitPrice, currency: shop.currency },
                shop.currency === 'USD' ? 'KHR' : 'USD',
                shop.exchangeRateUsdKhr,
              ),
            ),
          })}
        </p>
      </div>

      {colors.length > 0 ? (
        <OptionGroup
          label={t('storefront.selectColor')}
          values={colors}
          selected={color}
          onSelect={setColor}
          swatchFor={(value) => variants.find((v) => v.color === value)?.colorHex ?? null}
          availableFor={(value) =>
            variants.some((v) => v.color === value && v.stock > 0 && (!size || v.size === size))
          }
        />
      ) : null}

      {sizes.length > 0 ? (
        <OptionGroup
          label={t('storefront.selectSize')}
          values={sizes}
          selected={size}
          onSelect={setSize}
          availableFor={(value) =>
            variants.some((v) => v.size === value && v.stock > 0 && (!color || v.color === color))
          }
        />
      ) : null}

      {/* §4 F&B: required-pick-1 / pick-0..N, priced live. */}
      {modifierGroups.length > 0 ? (
        <ModifierPicker
          groups={modifierGroups}
          selection={modifiers}
          onChange={setModifiers}
          currency={shop.currency}
          locale={locale}
          t={t}
        />
      ) : null}

      {/* §4 services: hours − bookings − buffer, resolved server-side. */}
      {service ? (
        <SlotPicker
          shopSlug={shop.slug}
          productId={product.id}
          service={service}
          value={slot}
          onChange={setSlot}
          locale={locale}
          t={t}
        />
      ) : null}

      {fnb && !fnb.isSoldOut && fnb.isSellingNow && fnb.prepMinutes > 0 ? (
        <p className="text-xs opacity-70">
          {t('storefront.prepTime', { count: fnb.prepMinutes })}
        </p>
      ) : null}

      {selected && selected.stock > 0 && selected.stock <= 3 ? (
        <Badge tone="accent">{t('common.lowStock', { count: selected.stock })}</Badge>
      ) : null}
      {soldOut ? <Badge tone="warn">{t('common.outOfStock')}</Badge> : null}

      {pickText(product.description, locale) ? (
        <p className="whitespace-pre-line text-sm opacity-80">
          {pickText(product.description, locale)}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-dvh pb-28">
      <ShopHeader showBack />
      {layout.product_detail === 'info-first' ? (
        <>
          {info}
          <div className="pt-4">{gallery}</div>
        </>
      ) : layout.product_detail === 'bottom-sheet' ? (
        /*
         * §4's GrabFood pattern, allowed for F&B only. The photo stays put and
         * the choices ride a panel over it, so a customer working through four
         * modifier groups never loses sight of what they are ordering — and
         * the sticky bar below stays reachable with one thumb.
         */
        <div className="relative">
          {gallery}
          <div
            className="relative -mt-6 rounded-t-3xl pb-2"
            style={{
              background: 'var(--color-bg)',
              boxShadow: '0 -8px 24px -12px rgb(0 0 0 / 0.25)',
            }}
          >
            <div
              className="mx-auto mt-2 h-1 w-10 rounded-full"
              style={{ background: 'var(--color-border)' }}
              aria-hidden="true"
            />
            {info}
          </div>
        </div>
      ) : (
        <>
          {gallery}
          {info}
        </>
      )}

      <StickyBar>
        <Button disabled={!canAdd} onClick={addToCart}>
          {actionLabel()}
        </Button>
      </StickyBar>

      <ShopFooter />
    </div>
  );
}

function OptionGroup({
  label,
  values,
  selected,
  onSelect,
  availableFor,
  swatchFor,
}: {
  label: string;
  values: string[];
  selected: string | null;
  onSelect: (value: string) => void;
  availableFor: (value: string) => boolean;
  swatchFor?: (value: string) => string | null;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium opacity-75">{label}</legend>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => {
          const available = availableFor(value);
          const active = selected === value;
          const swatch = swatchFor?.(value) ?? null;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onSelect(value)}
              // Unavailable options stay visible but obviously dead — hiding
              // them makes a shopper think the shop simply does not stock it.
              disabled={!available}
              className="flex items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-sm font-medium"
              style={{
                border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: active ? 'var(--color-primary)' : 'transparent',
                color: active ? 'var(--color-primary-contrast)' : 'var(--color-text)',
                opacity: available ? 1 : 0.35,
                textDecoration: available ? 'none' : 'line-through',
              }}
            >
              {swatch ? (
                <span
                  className="size-4 rounded-full"
                  style={{ background: swatch, border: '1px solid var(--color-border)' }}
                  aria-hidden="true"
                />
              ) : null}
              {value}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
